/* eslint-disable arrow-body-style */
import { get } from 'lodash';
import React, { useState } from 'react';
import styled from 'styled-components';
import { useSelector } from 'react-redux';
import { FaCaretDown, FaCaretUp } from 'react-icons/fa';
import { QUARTER_CONTRAST, HALF_CONTRAST, CONTRAST } from '../../theme';
import { LayerFilters } from './layer-filters';
import { formatLabel } from './formats';

const KeysContainer = styled.div`
  overflow-y: auto;
  background-color: white;
  border-radius: 4px;
  padding: 12px;
  box-shadow: 1px 2px 2px ${QUARTER_CONTRAST};
  margin-bottom: 6px;
  pointer-events: all;
  max-height: calc(50% - 6px);
  flex-direction: column;
`;

const LegendWrapper = styled.div`
  pointer-events: none;
  top: 8px;
  right: 50px;
  width: 180px;
  position: absolute;
  height: calc(100vh - 180px);
  display: flex;
  flex-direction: column;
`;

const KeyContainer = styled.div`
  flex-direction: row;
  display: flex;
  align-items: center;
  padding-bottom: 2px;
`;

const Label = styled.div`
  color: ${HALF_CONTRAST};
  font-size: 13px;
  font-weight: 600;
  padding-top: 4px;
  padding-bottom: 4px;
`;

const Color = styled.div`
  height: 14px;
  width: 14px;
  min-width: 14px;
  margin-right: 6px;
  margin-left: 6px;
`;

export const SmallLabel = styled.div`
  color: ${CONTRAST};
  font-size: 12px;
  word-break: break-all;
`;
export const Arrow = styled.div`
  color: ${HALF_CONTRAST};
  font-size: 16px;
  padding-left: 6px;
`;
const Value = styled.div`
  color: ${CONTRAST};
  font-size: 12px;
`;

const Header = styled.div`
  color: ${CONTRAST};
  font-size: 12px;
  font-weight: 600;
  padding-bottom: 4px;
  cursor: pointer;
  display: flex;
  flex-direction: row;
  justify-content: space-between;
  align-items: center;
`;

export const Section = styled.div`
  flex-direction: column;
  display: flex;

  &:last-of-type {
    padding-bottom: 0;
  }
`;

const DEFAULT_STYLE = {
  type: 'line',
  paint: {
    'line-color': '#E2603B',
  },
};

export const getKey = (style, unit = '', map, orLess, discrete) => {
  if (
    style.type === 'fill' ||
    style.type === 'line' ||
    style.type === 'circle'
  ) {
    let stops = [];

    if (get(style, `paint["${style.type}-color"].stops`)) {
      stops = get(style, `paint["${style.type}-color"].stops`);
    } else if (get(style, `paint["${style.type}-color"]`)) {
      stops = [['', get(style, `paint["${style.type}-color"]`)]];
    }

    if (!stops[0]) return;
    const containsNums = typeof stops[0][0] === 'number';

    if (stops && stops.length && containsNums) {
      stops = [...stops].reverse();
    }

    if (orLess != null && containsNums) {
      stops = stops.filter(([label]) => parseFloat(label, 10) > orLess);
      stops.push(stops[stops.length - 1]);
    }

    return stops.map(([label, color], i) => {
      if (label === Infinity) return null;
      const formattedLabel = formatLabel(label, unit, map);
      const isNum = typeof label === 'number' && !map;
      let finalLabel = formattedLabel;
      const prevValue = stops[i - 1] && stops[i - 1][0];

      if (discrete) {
        finalLabel = `${formattedLabel}`;
      } else if (isNum && prevValue === Infinity) {
        finalLabel = `${formattedLabel} +`;
      } else if (isNum && i === stops.length - 1 && orLess != null) {
        finalLabel = `${formattedLabel} or less`;
      } else if (isNum && typeof prevValue === 'number') {
        const prevLabel = formatLabel(stops[i - 1][0], unit, map);
        finalLabel = `${formattedLabel} to ${prevLabel}`;
      }

      const { type } = style;
      const dashed = !!style.paint['line-dasharray'];

      return (
        <KeyContainer key={`${finalLabel}-${style}`}>
          {`${finalLabel}`.trim().length ? null : <Arrow>↳</Arrow>}
          <Color
            color={color}
            type={style.type}
            dashed={dashed}
            style={{
              borderRadius: type === 'circle' ? '8px' : '2px',
              backgroundColor:
                type === 'circle' || type === 'fill' ? color : 'transparent',
              border: type === 'line' ? color : 'transparent',
              borderStyle: dashed ? 'dashed' : 'solid',
              borderWidth: 1,
            }}
          />
          {finalLabel || finalLabel === 0 ? (
            <SmallLabel>{finalLabel}</SmallLabel>
          ) : null}
        </KeyContainer>
      );
    });
  }

  return null;
};

const mapState = ({
  filters,
  customLayers,
  newCustomLayers,
  filtersSelected,
}) => ({
  filterDefinitions: filters,
  customLayers: { ...customLayers, ...newCustomLayers },
  filters: Object.keys(filtersSelected).map((key) => {
    const filter = filters.find((f) => f.key === key);
    const filterSelected = filtersSelected[key];
    let value = '';

    if (filter.options) {
      const options = filter.options.find((option) => {
        return (
          option.value === filterSelected.eq ||
          filterSelected.eq.includes(option.value)
        );
      });
      if (options) value = options.label;
    }
    if (filterSelected.gte) {
      value += `Over ${filterSelected.gte}. `;
    }
    if (filterSelected.lte) {
      value += `Under ${filterSelected.lte}. `;
    }
    return {
      value,
      label: filter.label,
    };
  }),
});

export const Key = ({ layers, selectedSurveyLayers }) => {
  const layersList = Object.values(layers).filter(layer => (
    layer.key !== 'highlighted_parcel' &&
    layer.key !== 'road_label_layer' &&
    layer.key !== 'road_layer'
  ));
  const [layersOpen, setLayersOpen] = useState(true);
  const [filtersOpen, setFiltersOpen] = useState(true);

  const { filters, customLayers = {}, filterDefinitions } = useSelector(
    mapState,
  );

  if (!layersList.length) return null;

  const customLayerList = layersList.reduce((acc, layer) => {
    if (layer.isCustom) acc.push(customLayers[layer.key]);
    return acc;
  }, []);

  return (
    <LegendWrapper>
      <KeysContainer>
        {layersList.length ? (
          <Header onClick={() => setLayersOpen(!layersOpen)}>
            Legend
            {layersOpen ? <FaCaretUp /> : <FaCaretDown />}
          </Header>
        ) : null}
        {layersOpen
          ? Object.values(selectedSurveyLayers).map(({ name, id }) => {
              return (
                <Section key={id}>
                  <Label>{name}</Label>
                  {getKey(DEFAULT_STYLE)}
                </Section>
              );
            })
          : null}
        {layersOpen
          ? layersList.reverse().map(({
              style, label, key, unit, map, orLess, discrete,
            }) => {
              return (
                <Section key={key}>
                  {label ? (<Label>{label}</Label>) : null}
                  {getKey(style, unit, map, orLess, discrete)}
                </Section>
              );
            })
          : null}
      </KeysContainer>
      {customLayerList.length || filters.length ? (
        <KeysContainer>
          <Header onClick={() => setFiltersOpen(!filtersOpen)}>
            Filters
            {filtersOpen ? <FaCaretUp /> : <FaCaretDown />}
          </Header>
          {filtersOpen
            ? filters.map((filter) => {
                const { value, label } = filter;
                return (
                  <Section key={label}>
                    <Label>{label}</Label>
                    <Value>{value}</Value>
                  </Section>
                );
              })
            : null}
          {filtersOpen
            ? customLayerList.map((layer) => (
                <LayerFilters
                  layer={layer}
                  key={`${layer.key}-layer-filters`}
                  filterDefinitions={filterDefinitions}
                />
              ))
            : null}
        </KeysContainer>
      ) : null}
    </LegendWrapper>
  );
};
